import { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Link, useNavigate } from "react-router-dom";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import moment from "moment";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import CombinedDateTimePicker from "../../components/CustomComponents/DateTimePicker/CombinedDateTimePicker";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

function AddVoucher() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const { hasInitiallyLoaded } = useProfileAndSettings();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  
  const [compCurrency, setCompCurrency] = useState("GBP"); // Added state for CompCurrency
  const [voucherData, setVoucherData] = useState({
    Voucher_Name: "",
    Voucher_Code: "",
    Voucher_Detail: "",
    Voucher_DiscountPercentage: "",
    Voucher_MinimumOrderTotalAmount: "",
    Voucher_MaxUsesLimit: "",
    Voucher_NoOfUses: "",
    Voucher_Active: false,
    MultiAllow_Active: false,
    Voucher_IsMobileActive: false,
    Voucher_StartDate: null,
    Voucher_EndDate: null,
  });
  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      Voucher_Name: voucherData.Voucher_Name || "",
      Voucher_Code: voucherData.Voucher_Code || "",
      Voucher_Detail: voucherData.Voucher_Detail || "",
      Voucher_DiscountPercentage: voucherData.Voucher_DiscountPercentage ?? "",
      Voucher_MinimumOrderTotalAmount: voucherData.Voucher_MinimumOrderTotalAmount ?? "",
      Voucher_MaxUsesLimit: voucherData.Voucher_MaxUsesLimit ?? "",
      Voucher_NoOfUses: voucherData.Voucher_NoOfUses ?? "",
      Voucher_Active: Boolean(voucherData.Voucher_Active),
      MultiAllow_Active: Boolean(voucherData.MultiAllow_Active),
      Voucher_IsMobileActive: Boolean(voucherData.Voucher_IsMobileActive),
      Voucher_StartDate: voucherData.Voucher_StartDate
        ? moment(voucherData.Voucher_StartDate).format()
        : null,
      Voucher_EndDate: voucherData.Voucher_EndDate
        ? moment(voucherData.Voucher_EndDate).format()
        : null,
    });
  }, [voucherData]);

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Fetch CompCurrency (only show error after initial load attempt; avoid toast while profile is still loading)
  const fetchCompCurrency = useCallback(async () => {
    try {
      if (!hasInitiallyLoaded) return;
      if (!profileData) {
        showErrorToast("Profile data not available. Please refresh the page.");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, [profileData, hasInitiallyLoaded]);

  useEffect(() => {
    fetchCompCurrency(); // Fetch currency on component mount
  }, [fetchCompCurrency]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Prevent negative values for numeric fields
    const numValue = parseFloat(value);
    const numericFields = [
      "Voucher_DiscountPercentage",
      "Voucher_MinimumOrderTotalAmount",
      "Voucher_MaxUsesLimit",
      "Voucher_NoOfUses"
    ];
    if (numericFields.includes(name)) {
      if (!isNaN(numValue) && numValue < 0) {
        newValue = "0";
      }
      if (name === "Voucher_DiscountPercentage" && !isNaN(numValue) && numValue > 100) {
        newValue = "100";
      }
      if (name === "Voucher_MaxUsesLimit" && !isNaN(numValue) && numValue < 1) {
        newValue = "1";
      }
    }

    setVoucherData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleToggleChange = (checked, name) => {
    setVoucherData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setVoucherData((prev) => {
      const momentValue = moment(value);
      const newVoucherData = { ...prev, [field]: momentValue };

      if (field === "Voucher_StartDate" && newVoucherData.Voucher_EndDate) {
        const endDateTime = moment(newVoucherData.Voucher_EndDate);
        if (endDateTime.isBefore(momentValue)) {
          newVoucherData.Voucher_EndDate = momentValue.clone();
        }
      }

      return newVoucherData;
    });
  };

  // Note: Date validation is now handled by CombinedDateTimePicker's built-in validation

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;
    if (!voucherData.Voucher_Name.trim()) {
      showErrorToast("Voucher name is required");
      return false;
    }

    if (!voucherData.Voucher_Code.trim()) {
      showErrorToast("Voucher code is required");
      return false;
    }

    if (
      !voucherData.Voucher_DiscountPercentage ||
      isNaN(voucherData.Voucher_DiscountPercentage) ||
      parseFloat(voucherData.Voucher_DiscountPercentage) < 0 ||
      parseFloat(voucherData.Voucher_DiscountPercentage) > 100
    ) {
      showErrorToast("Valid discount percentage between 0 and 100 is required");
      return false;
    }

    if (!voucherData.Voucher_StartDate) {
      showErrorToast("Start date is required");
      return false;
    }

    if (!voucherData.Voucher_EndDate) {
      showErrorToast("End date is required");
      return false;
    }

    if (moment(voucherData.Voucher_EndDate).isBefore(voucherData.Voucher_StartDate)) {
      showErrorToast("End date cannot be before start date");
      return false;
    }

    if (voucherData.Voucher_Detail.length > 250) {
      showErrorToast("Voucher detail must not exceed 250 characters");
      return false;
    }

    if (
      voucherData.Voucher_NoOfUses &&
      (isNaN(voucherData.Voucher_NoOfUses) || parseInt(voucherData.Voucher_NoOfUses) < 0)
    ) {
      showErrorToast("Number of uses must be a non-negative number");
      return false;
    }

    if (
      voucherData.Voucher_MaxUsesLimit !== "" &&
      voucherData.Voucher_MaxUsesLimit !== null &&
      voucherData.Voucher_MaxUsesLimit !== undefined
    ) {
      const maxUsesLimit = parseInt(voucherData.Voucher_MaxUsesLimit);
      if (isNaN(maxUsesLimit) || maxUsesLimit < 1) {
        showErrorToast("Maximum uses limit must be 1 or greater");
        return false;
      }
    }

    if (
      voucherData.Voucher_MinimumOrderTotalAmount &&
      (isNaN(voucherData.Voucher_MinimumOrderTotalAmount) ||
        parseFloat(voucherData.Voucher_MinimumOrderTotalAmount) < 0)
    ) {
      showErrorToast("Minimum order total must be a non-negative number");
      return false;
    }

    setLoading(true);

    try {
      const voucherPayload = {
        Voucher_ID: 0,
        CompID: CompID,
        Voucher_Name: voucherData.Voucher_Name,
        Voucher_Code: voucherData.Voucher_Code,
        Voucher_Detail: voucherData.Voucher_Detail,
        Voucher_DiscountPercentage:
          parseFloat(voucherData.Voucher_DiscountPercentage) || 0,
        Voucher_MinimumOrderTotalAmount:
          parseFloat(voucherData.Voucher_MinimumOrderTotalAmount) || 0,
        Voucher_StartDate: moment(voucherData.Voucher_StartDate).format(),
        Voucher_EndDate: moment(voucherData.Voucher_EndDate).format(),
        Voucher_MaxUsesLimit: parseInt(voucherData.Voucher_MaxUsesLimit) || 0,
        Voucher_NoOfUses: parseInt(voucherData.Voucher_NoOfUses) || 0,
        Voucher_Active: voucherData.Voucher_Active,
        UpdateDateTime: new Date().toISOString(),
        MultiAllow_Active: voucherData.MultiAllow_Active,
        Voucher_IsMobileActive: voucherData.Voucher_IsMobileActive,
      };

      const response = await axiosInstance.post(
        APIs.POST_ADD_VOUCHER,
        voucherPayload
      );

      if (!response.data.status) {
        showErrorToast(response.data.message || "Failed to add voucher");
        return false;
      }

      showSuccessToast("Voucher added successfully");
      initialFormSnapshotRef.current = getFormSnapshot();
      if (exitAfterSave) {
        navigate("/voucher/manageVoucher");
      }
      return true;
    } catch (error) {
      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.errors?.join(", ") ||
        "An error occurred while adding the voucher";
      showErrorToast(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/voucher/manageVoucher">Manage Vouchers</Link>
            </li>
            <li className="breadcrumb-item active">Create Voucher</li>
          </ol>
          <h1 className="page-header mb-0">Create Voucher</h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/voucher/manageVoucher")}
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>

          <PanelBody>
            <div className="row mb-4">
              <div className="col-6">
                <FormInput
                  label="Voucher Name"
                  placeHolder="Enter voucher name"
                  type="text"
                  name="Voucher_Name"
                  value={voucherData.Voucher_Name}
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Voucher Code"
                  placeHolder="Enter voucher code"
                  type="text"
                  name="Voucher_Code"
                  value={voucherData.Voucher_Code}
                  onchange={handleInputChange}
                />
              </div>
            </div>

            <div className="row mb-4">
              {/* <div className="col-6">
                <FormInput
                  label="Number of Uses"
                  placeHolder="e.g. 0"
                  type="number"
                  name="Voucher_NoOfUses"
                  value={voucherData.Voucher_NoOfUses}
                  onchange={handleInputChange}
                />
              </div> */}

             
            </div>

            <div className="row mb-4">
              <div className="col-6 mb-2">
                <FormInput
                  label={`Minimum Order Total (${getCurrencySymbol(compCurrency)})`} // Updated label
                  placeHolder={`${getCurrencySymbol(compCurrency)}0.00`} // Updated placeholder
                  type="number"
                  name="Voucher_MinimumOrderTotalAmount"
                  value={voucherData.Voucher_MinimumOrderTotalAmount}
                  onchange={handleInputChange}
                  prepend={getCurrencySymbol(compCurrency)} // Added prepend prop
                  min={0}
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Maximum Uses Limit"
                  placeHolder="e.g. 100"
                  type="number"
                  name="Voucher_MaxUsesLimit"
                  value={voucherData.Voucher_MaxUsesLimit}
                  onchange={handleInputChange}
                  min={1}
                />
              </div>
            </div>

            <div className="row mb-4">
               <div className="col-6  ">
                <FormInput
                  label="Discount Percentage"
                  placeHolder="e.g. 10%"
                  type="number"
                  name="Voucher_DiscountPercentage"
                  value={voucherData.Voucher_DiscountPercentage}
                  onchange={handleInputChange}
                  min={0}
                  max={100}
                />
              </div>
              <div className="col-6">
                <label 
                  className="form-label fw-medium"
                  style={{ color: isDarkMode ? '#ffffff' : '#000000' }}
                >
                  <h6>Voucher Detail</h6>
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter voucher description"
                  name="Voucher_Detail"
                  value={voucherData.Voucher_Detail}
                  onChange={handleInputChange}
                  style={{
                    borderRadius: "10px",
                    border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                    background: isDarkMode ? "#2d3436" : "#F9F9F9",
                    color: isDarkMode ? "#ffffff" : "#000000",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = themeColor;
                    e.target.style.boxShadow = `0 0 0 0.2rem ${themeColor}40`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isDarkMode ? "#404040" : "#D9D9D9";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-6">
                <CombinedDateTimePicker
                  label="Start Date"
                  value={voucherData.Voucher_StartDate}
                  onChange={(date) =>
                    handleDateRangeChange("Voucher_StartDate", date)
                  }
                  ampm={false}
                  placeholder="Select start date and time"
                  name="Voucher_StartDate"
                />
              </div>
              <div className="col-6">
                <CombinedDateTimePicker
                  label="End Date"
                  value={voucherData.Voucher_EndDate}
                  onChange={(date) =>
                    handleDateRangeChange("Voucher_EndDate", date)
                  }
                  ampm={false}
                  placeholder="Select end date and time"
                  name="Voucher_EndDate"
                  minDate={voucherData.Voucher_StartDate}
                />
              </div>
            </div>

            <div className="row mb-4">
              {/* Hidden: Allow Multiple Uses - not supported yet (no per-customer usage tracking) */}
              {false && (
              <div className="col-4">
                <ToggleSwitch
                  isChecked={voucherData.MultiAllow_Active}
                  onToggle={(checked) => handleToggleChange(checked, "MultiAllow_Active")}
                  label="Allow Multiple Uses"
                  name="MultiAllow_Active"
                  themeColor={themeColor}
                />
                <p className="text-muted mt-2">
                  Allow multiple uses of the voucher per customer
                </p>
              </div>
              )}
              <div className="col-4">
                <ToggleSwitch
                  isChecked={voucherData.Voucher_IsMobileActive}
                  onToggle={(checked) => handleToggleChange(checked, "Voucher_IsMobileActive")}
                  label="Online Active"
                  name="Voucher_IsMobileActive"
                  themeColor={themeColor}
                />
                <p className="text-muted mt-2">
                  Allow voucher use on online platforms
                </p>
              </div>
              <div className="col-4">
                <ToggleSwitch
                  isChecked={voucherData.Voucher_Active}
                  onToggle={(checked) => handleToggleChange(checked, "Voucher_Active")}
                  label="Active"
                  name="Voucher_Active"
                  themeColor={themeColor}
                />
                <p className="text-muted mt-2">Enable or disable the voucher</p>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>
    </div>
  );
}

export default AddVoucher;